import { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ClientCombobox } from "@/components/ClientCombobox";
import { SavedViews } from "@/components/SavedViews";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { socSeverityColor, socStatusColor, socStatusLabel, SOC_STATUS_OPTIONS, SOC_SEVERITIES, SOC_SOURCES, fmtIST } from "@/lib/utils";
import { Plus, MagnifyingGlass, ShieldWarning, HandGrabbing, ArrowFatLinesUp } from "@phosphor-icons/react";
import { toast } from "sonner";

const ageLabel = (iso) => {
  if (!iso) return "—";
  const d = Math.floor((Date.now() - new Date(iso)) / 86400000);
  if (d <= 0) {
    const h = Math.floor((Date.now() - new Date(iso)) / 3600000);
    return h <= 0 ? "just now" : `${h}h`;
  }
  return `${d}d`;
};

export default function SocCases() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [cases, setCases] = useState([]);
  const [clients, setClients] = useState([]);
  const [categories, setCategories] = useState([]);
  const [teams, setTeams] = useState([]);
  const [sp] = useSearchParams();
  const EMPTY_F = { status: "", severity: "", client: "all", q: "", tag: "", date_from: "", date_to: "", team: "all", escalated: false, assignee: "", sla: "", classification: "" };
  const [f, setF] = useState({
    ...EMPTY_F,
    status: sp.get("status") || "",
    client: sp.get("client") || "all",
    team: sp.get("my_team") === "1" ? "mine" : "all",
    escalated: sp.get("escalated") === "1",
    assignee: sp.get("assignee") || "",
    sla: sp.get("sla") || "",
    classification: sp.get("classification") || "",
    severity: sp.get("severity") || "",
    date_from: sp.get("date_from") || "",
    date_to: sp.get("date_to") || "",
  });
  const [escCount, setEscCount] = useState(0);
  const [sort, setSort] = useState({ key: "created_at", dir: "desc" });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", severity: "Medium", category: "Other", source: "Manual", client: "Unassigned", reporter: "" });
  const [saving, setSaving] = useState(false);
  const [autoHint, setAutoHint] = useState("");
  const autoClient = async () => {
    const email = form.reporter.trim();
    if (!email.includes("@") || (form.client && form.client !== "Unassigned")) return;
    try {
      const { data } = await api.get(`/soc/client-for-sender?email=${encodeURIComponent(email)}`);
      if (data.matched) { setForm(f => ({ ...f, client: data.client })); setAutoHint(`Client set to ${data.client} from the sender's domain`); }
      else setAutoHint("No client matches this domain — select one manually");
    } catch { /* ignore */ }
  };
  const takeCase = async (c) => {
    try { await api.post(`/soc/cases/${c.id}/take`); toast.success(`${c.number} is now assigned to you`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not take case"); load(); }
  };

  const load = useCallback(() => {
    const p = new URLSearchParams();
    if (f.status) p.set("status", f.status);
    if (f.severity) p.set("severity", f.severity);
    if (f.client && f.client !== "all") p.set("client", f.client);
    if (f.q) p.set("q", f.q);
    if (f.tag) p.set("tag", f.tag);
    if (f.date_from) p.set("date_from", f.date_from);
    if (f.date_to) p.set("date_to", f.date_to);
    if (f.team === "mine") p.set("my_team", "true");
    else if (f.team && f.team !== "all") p.set("team", f.team);
    if (f.escalated) p.set("escalated", "true");
    if (f.assignee) p.set("assignee", f.assignee);
    if (f.sla) p.set("sla", f.sla);
    if (f.classification) p.set("classification", f.classification);
    api.get(`/soc/cases?${p.toString()}`).then(r => setCases(r.data)).catch(() => {});
  }, [f]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get("/soc/cases?escalated=true").then(r => setEscCount((r.data || []).filter(c => c.status !== "closed").length)).catch(() => {});
  }, [cases]);
  useEffect(() => {
    if (user?.role === "soc_auditor") return;
    api.get("/client-registry?module=soc").then(r => setClients(r.data)).catch(() => {});
    api.get("/soc/settings").then(r => setCategories(r.data.categories || [])).catch(() => {});
    api.get("/soc/teams").then(r => setTeams(r.data)).catch(() => {});
  }, [user]);

  const create = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/soc/cases", form);
      toast.success(`Case ${data.number} created`);
      setOpen(false);
      setForm({ title: "", description: "", severity: "Medium", category: "Other", source: "Manual", client: "Unassigned", reporter: "" });
      nav(`/soc/cases/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not create case"); }
    finally { setSaving(false); }
  };

  const SEV_RANK = { Critical: 0, High: 1, Medium: 2, Low: 3 };
  const STATUS_RANK = { new: 0, triage: 1, investigating: 2, containment: 3, eradication_recovery: 4, resolved: 5, closed: 6 };
  const toggleSort = (key) => setSort(s => ({ key, dir: s.key === key && s.dir === "asc" ? "desc" : "asc" }));
  const sortArrow = (key) => sort.key === key ? (sort.dir === "asc" ? " ▲" : " ▼") : "";
  const sortedCases = [...cases].sort((a, b) => {
    const dir = sort.dir === "asc" ? 1 : -1;
    if (sort.key === "severity") return (SEV_RANK[a.severity] - SEV_RANK[b.severity]) * dir;
    if (sort.key === "status") return (STATUS_RANK[a.status] - STATUS_RANK[b.status]) * dir;
    // age: older case = older created_at; sort by created_at timestamp
    return ((new Date(a.created_at) - new Date(b.created_at))) * dir;
  });
  const showWorking = user && user.role !== "soc_auditor" && !["resolved", "closed", "closed_any"].includes(f.status);
  const isWorking = (c) => c.assignee_id === user?.id && !["resolved", "closed"].includes(c.status);
  const myCases = showWorking ? sortedCases.filter(isWorking) : [];
  const otherCases = showWorking ? sortedCases.filter(c => !isWorking(c)) : sortedCases;

  return (
    <div className="p-6 space-y-5" data-testid="soc-cases-view">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-widest text-rag-red mb-1">Security Operations</div>
          <h1 className="font-display text-3xl font-black flex items-center gap-2"><ShieldWarning size={28} /> Cases</h1>
        </div>
        <Sheet open={open} onOpenChange={setOpen}>
          {user?.role !== "soc_auditor" && (
            <SheetTrigger asChild>
              <Button data-testid="soc-new-case-btn" className="rounded-sm font-mono uppercase text-xs"><Plus size={15} className="mr-1" /> New Case</Button>
            </SheetTrigger>
          )}
          <SheetContent className="w-full sm:max-w-lg overflow-y-auto" data-testid="soc-new-case-sheet">
            <SheetHeader><SheetTitle>New SOC Case</SheetTitle></SheetHeader>
            <div className="space-y-3 mt-4">
              <div>
                <label className="text-xs font-mono text-muted-foreground">Title *</label>
                <Input data-testid="soc-form-title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} className="rounded-sm mt-1" />
              </div>
              <div>
                <label className="text-xs font-mono text-muted-foreground">Description</label>
                <Textarea data-testid="soc-form-desc" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} className="rounded-sm mt-1 min-h-[90px]" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-mono text-muted-foreground">Severity</label>
                  <Select value={form.severity} onValueChange={v => setForm({ ...form, severity: v })}>
                    <SelectTrigger data-testid="soc-form-severity" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{SOC_SEVERITIES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-mono text-muted-foreground">Source</label>
                  <Select value={form.source} onValueChange={v => setForm({ ...form, source: v })}>
                    <SelectTrigger data-testid="soc-form-source" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{SOC_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-mono text-muted-foreground">Category</label>
                  <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="soc-form-category" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{categories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-xs font-mono text-muted-foreground">Client</label>
                  <Select value={form.client} onValueChange={v => setForm({ ...form, client: v })}>
                    <SelectTrigger data-testid="soc-form-client" className="rounded-sm mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Unassigned">Unassigned</SelectItem>
                      {clients.map(c => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-mono text-muted-foreground">Reporter (email)</label>
                <Input data-testid="soc-form-reporter" value={form.reporter} onChange={e => setForm({ ...form, reporter: e.target.value })} onBlur={autoClient} className="rounded-sm mt-1" />
                {autoHint && <div className="text-[10px] font-mono text-muted-foreground mt-1" data-testid="soc-form-client-hint">{autoHint}</div>}
              </div>
            </div>
            <SheetFooter className="mt-5">
              <Button data-testid="soc-create-case-submit" onClick={create} disabled={saving} className="rounded-sm font-mono uppercase text-xs w-full">
                {saving ? "Creating…" : "Create Case"}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
      </div>

      <Card className="p-3 border-border rounded-sm flex flex-wrap items-center gap-2">
        <div className="w-full"><SavedViews filters={f} onApply={setF} emptyFilters={EMPTY_F} /></div>
        <div className="relative flex-1 min-w-[180px]">
          <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-muted-foreground" />
          <Input data-testid="soc-search" value={f.q} onChange={e => setF({ ...f, q: e.target.value })} placeholder="Search cases…" className="rounded-sm h-9 pl-8 text-sm" />
        </div>
        <div className="flex items-center gap-1.5" data-testid="soc-severity-chips">
          {SOC_SEVERITIES.map(s => {
            const active = f.severity === s;
            return (
              <button key={s} data-testid={`soc-sev-chip-${s}`} type="button"
                onClick={() => setF({ ...f, severity: active ? "" : s })}
                aria-pressed={active}
                className={`text-[10px] font-mono uppercase tracking-wide px-2.5 h-9 rounded-sm border transition-all ${active ? socSeverityColor(s) + " font-bold shadow-sm" : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"}`}>
                {s}
              </button>
            );
          })}
        </div>
        <button type="button" data-testid="soc-quick-tier2" aria-pressed={f.escalated}
          onClick={() => setF({ ...f, escalated: !f.escalated })}
          title="Cases handed off to Tier 2"
          className={`inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wide px-2.5 h-9 rounded-sm border transition-all ${f.escalated ? "border-rag-amber-fg bg-rag-amber-bg text-rag-amber-fg font-bold shadow-sm" : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"}`}>
          <ArrowFatLinesUp size={13} weight={f.escalated ? "fill" : "regular"} /> Tier 2 escalations
          {escCount > 0 && <span data-testid="soc-quick-tier2-count" className={`px-1.5 py-0.5 rounded-full text-[9px] leading-none ${f.escalated ? "bg-rag-amber-fg text-background" : "bg-rag-amber-bg text-rag-amber-fg"}`}>{escCount}</span>}
        </button>
        <div className="flex items-center gap-1.5 flex-wrap" data-testid="soc-status-chips">
          <button type="button" data-testid="soc-status-chip-open" aria-pressed={f.status === "open"}
            onClick={() => setF({ ...f, status: f.status === "open" ? "" : "open" })}
            title="All cases not yet resolved or closed"
            className={`text-[10px] font-mono uppercase tracking-wide px-2.5 h-9 rounded-sm border transition-all ${f.status === "open" ? "border-rag-amber-fg bg-rag-amber-bg text-rag-amber-fg font-bold shadow-sm" : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"}`}>
            Open cases
          </button>
          <span className="w-px h-6 bg-border mx-0.5" />
          {SOC_STATUS_OPTIONS.map(s => {
            const active = f.status === s.value;
            return (
              <button key={s.value} data-testid={`soc-status-chip-${s.value}`} type="button"
                onClick={() => setF({ ...f, status: active ? "" : s.value })}
                aria-pressed={active}
                className={`text-[10px] font-mono uppercase tracking-wide px-2.5 h-9 rounded-sm border transition-all ${active ? socStatusColor(s.value) + " font-bold shadow-sm" : "border-border text-muted-foreground hover:border-foreground/40 hover:text-foreground"}`}>
                {s.label}
              </button>
            );
          })}
        </div>
        <ClientCombobox value={f.client} onChange={v => setF({ ...f, client: v })} clients={clients} className="w-48" testid="soc-filter-client" />
        <Select value={f.team} onValueChange={v => setF({ ...f, team: v })}>
          <SelectTrigger data-testid="soc-filter-team" className="w-40 rounded-sm h-9 text-xs"><SelectValue placeholder="Team" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All teams</SelectItem>
            <SelectItem value="mine">My team</SelectItem>
            {teams.map(t => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Input data-testid="soc-filter-tag" value={f.tag} onChange={e => setF({ ...f, tag: e.target.value })} placeholder="Tag…" className="rounded-sm h-9 text-sm w-32" />
        <div className="flex items-center gap-1">
          <Input data-testid="soc-filter-date-from" type="date" value={f.date_from} onChange={e => setF({ ...f, date_from: e.target.value })} className="rounded-sm h-9 text-xs w-36" title="Created from" />
          <span className="text-xs text-muted-foreground">–</span>
          <Input data-testid="soc-filter-date-to" type="date" value={f.date_to} onChange={e => setF({ ...f, date_to: e.target.value })} className="rounded-sm h-9 text-xs w-36" title="Created to" />
        </div>
        {(f.assignee || f.sla || f.classification) && (
          <div className="flex items-center gap-1.5" data-testid="soc-meta-filters">
            {f.assignee === "unassigned" && <button type="button" onClick={() => setF({ ...f, assignee: "" })} className="text-[10px] font-mono uppercase px-2 h-9 rounded-sm border border-primary/50 text-primary">Unassigned ×</button>}
            {f.sla && <button type="button" onClick={() => setF({ ...f, sla: "" })} className={`text-[10px] font-mono uppercase px-2 h-9 rounded-sm border ${f.sla === "breached" ? "border-rag-red-fg/60 text-rag-red-fg" : "border-rag-amber-fg/60 text-rag-amber-fg"}`}>SLA {f.sla === "breached" ? "breached" : "due soon"} ×</button>}
            {f.classification && <button type="button" onClick={() => setF({ ...f, classification: "" })} className="text-[10px] font-mono uppercase px-2 h-9 rounded-sm border border-primary/50 text-primary">{f.classification} ×</button>}
          </div>
        )}
        {(f.tag || f.date_from || f.date_to || f.q || f.severity || f.status || f.client !== "all" || f.team !== "all" || f.escalated || f.assignee || f.sla || f.classification) && (
          <Button data-testid="soc-filter-reset" variant="ghost" className="rounded-sm text-xs font-mono uppercase h-9"
            onClick={() => setF({ ...EMPTY_F })}>Reset</Button>
        )}
      </Card>

      {showWorking && (
        <section data-testid="soc-working-grid">
          <div className="flex items-center gap-2 mb-2">
            <HandGrabbing size={14} className="text-primary" />
            <h2 className="text-[11px] font-mono uppercase tracking-widest text-foreground">My working cases</h2>
            <span data-testid="soc-working-count" className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-primary/10 text-primary">{myCases.length}</span>
            <span className="text-[10px] font-mono text-muted-foreground">assigned to you · matches the filters above</span>
          </div>
          {renderTable(myCases, "No cases assigned to you. Use Take on a case below to start working on it.", "soc-working-table")}
        </section>
      )}

      <section data-testid="soc-queue-grid">
        {showWorking && (
          <div className="flex items-center gap-2 mb-2 mt-2">
            <h2 className="text-[11px] font-mono uppercase tracking-widest text-foreground">All other cases</h2>
            <span data-testid="soc-queue-count" className="text-[10px] font-mono px-1.5 py-0.5 rounded-sm bg-accent text-muted-foreground">{otherCases.length}</span>
          </div>
        )}
        {renderTable(showWorking ? otherCases : sortedCases, "No cases found.", "soc-cases-table")}
      </section>
    </div>
  );

  function renderTable(rows, emptyText, testid) {
    return (
      <Card className="border-border rounded-sm overflow-hidden">
        <table className="w-full text-sm" data-testid={testid}>
          <thead className="bg-accent/30 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
            <tr>
              <th className="text-left px-3 py-2.5">Number</th>
              <th className="text-left px-3 py-2.5">Title</th>
              <th className="text-left px-3 py-2.5">Client</th>
              <th className="text-left px-3 py-2.5 cursor-pointer select-none hover:text-foreground" data-testid={`${testid}-sort-severity`} onClick={() => toggleSort("severity")}>Severity{sortArrow("severity")}</th>
              <th className="text-left px-3 py-2.5 cursor-pointer select-none hover:text-foreground" data-testid={`${testid}-sort-status`} onClick={() => toggleSort("status")}>Status{sortArrow("status")}</th>
              <th className="text-left px-3 py-2.5">Category</th>
              <th className="text-left px-3 py-2.5">Assignee</th>
              <th className="text-left px-3 py-2.5 cursor-pointer select-none hover:text-foreground" data-testid={`${testid}-sort-age`} onClick={() => toggleSort("created_at")}>Age{sortArrow("created_at")}</th>
              <th className="text-left px-3 py-2.5">SLA</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(c => (
              <tr key={c.id} data-testid={`soc-case-row-${c.number}`} onClick={() => nav(`/soc/cases/${c.id}`)}
                className={`border-t border-border cursor-pointer ${c.severity === "Critical" ? "border-l-2 border-l-rag-red" : c.severity === "High" ? "border-l-2 border-l-rag-red/50" : "border-l-2 border-l-transparent"} ${c.resolution_breached ? "bg-rag-red-bg/60 hover:bg-rag-red-bg" : "hover:bg-accent/20"}`}>
                <td className="px-3 py-3 font-mono text-primary">{c.number}</td>
                <td className="px-3 py-3 max-w-[280px]">
                  <div className="truncate flex items-center gap-1.5">
                    {c.escalated_tier >= 2 && <span data-testid={`soc-t2-badge-${c.number}`} title={`Escalated to Tier 2 by ${c.escalated_by || "—"}`} className="shrink-0 inline-flex items-center gap-0.5 text-[9px] font-mono font-bold px-1.5 py-0.5 rounded-sm border border-rag-amber-fg/60 text-rag-amber-fg bg-rag-amber-bg"><ArrowFatLinesUp size={10} weight="fill" /> T2</span>}
                    <span className="truncate">{c.title}</span>
                  </div>
                  {c.possible_duplicate_number && (
                    <span data-testid={`soc-dup-badge-${c.number}`} className="inline-block mt-1 text-[9px] font-mono px-1.5 py-0.5 border border-rag-amber/50 text-rag-amber-fg rounded-sm">
                      possible duplicate of {c.possible_duplicate_number}
                    </span>
                  )}
                  {c.possibly_related_number && (
                    <span data-testid={`soc-related-badge-${c.number}`} className="inline-block mt-1 ml-1 text-[9px] font-mono px-1.5 py-0.5 border border-border text-muted-foreground rounded-sm">
                      possibly related to {c.possibly_related_number}
                    </span>
                  )}
                </td>
                <td className="px-3 py-3 text-xs">{c.client}</td>
                <td className="px-3 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 border ${socSeverityColor(c.severity)}`}>{c.severity}</span></td>
                <td className="px-3 py-3"><span className={`text-[10px] font-mono px-2 py-0.5 border ${socStatusColor(c.status)}`}>{socStatusLabel(c.status)}</span></td>
                <td className="px-3 py-3 text-xs">{c.category}</td>
                <td className="px-3 py-3 text-xs">
                  {c.assignee_name || (user?.role !== "soc_auditor" && c.status !== "closed"
                    ? <button data-testid={`soc-take-${c.number}`} onClick={(e) => { e.stopPropagation(); takeCase(c); }}
                        className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-primary/50 text-primary hover:bg-primary/10 transition-colors">
                        <HandGrabbing size={12} /> Take
                      </button>
                    : <span className="text-muted-foreground">—</span>)}
                </td>
                <td className="px-3 py-3 text-xs text-muted-foreground" title={fmtIST(c.created_at)}>{ageLabel(c.created_at)}</td>
                <td className="px-3 py-3 text-xs">{c.resolution_breached ? <span data-testid={`soc-sla-breached-${c.number}`} className="inline-flex items-center gap-1.5 text-rag-red-fg font-mono"><span className="h-1.5 w-1.5 rounded-full bg-rag-red animate-pulse" /> BREACHED</span> : c.sla_due_soon ? <span data-testid={`soc-sla-soon-${c.number}`} className="text-rag-amber-fg font-mono">DUE SOON</span> : <span className="text-rag-green-fg font-mono">OK</span>}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={9} className="px-3 py-10 text-center text-muted-foreground">{emptyText}</td></tr>}
          </tbody>
        </table>
      </Card>
    );
  }
}
